const AUTH_KEY = "auth";
const SNS_KEY = "sns-auth";
const TEN_YEARS = 60 * 60 * 24 * 365 * 10;

const cookieOptions = {
  maxAge: TEN_YEARS,
  sameSite: "lax",
  path: "/",
  secure: process.env.NODE_ENV === "production",
};

const readJson = (raw) => {
  if (!raw) return null;
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
};

const readLocal = (key) => {
  if (typeof localStorage === "undefined") return null;
  return readJson(localStorage.getItem(key));
};

const writeLocal = (key, data) => {
  if (typeof localStorage === "undefined") return;
  if (!data) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(data));
};

const syncStore = (cookie, key, hasValue) => {
  const local = readLocal(key);
  if (hasValue(local) && !cookie.value) {
    cookie.value = JSON.stringify(local);
  } else if (!hasValue(local) && cookie.value) {
    const parsed = readJson(cookie.value);
    writeLocal(key, parsed);
    if (parsed) cookie.value = JSON.stringify(parsed);
  }
};

export const useAuthState = () => {
  const authCookie = useCookie(AUTH_KEY, cookieOptions);
  const snsCookie = useCookie(SNS_KEY, cookieOptions);

  const authData = computed(() => {
    const local = readLocal(AUTH_KEY);
    if (local?.token) return local;
    return readJson(authCookie.value);
  });

  const snsData = computed(() => {
    const local = readLocal(SNS_KEY);
    if (local?.accessToken) return local;
    return readJson(snsCookie.value);
  });

  const token = computed(() => authData.value?.token || null);
  const user = computed(() => authData.value?.user || null);
  const loginType = computed(() => authData.value?.loginType || null);
  const isAuthenticated = computed(() => !!token.value);
  const snsAccessToken = computed(() => snsData.value?.accessToken || null);
  const snsUser = computed(() => snsData.value?.user || null);
  const snsType = computed(() => snsData.value?.type || null);
  const isKcbAuthed = computed(() => authData.value?.isKcbAuthed || false);
  const userIdx = computed(() => authData.value?.userIdx || null);

  const setAuth = (newToken, newUser = {}, nextLoginType = null, kcbAuthed = false) => {
    const payload = {
      token: newToken,
      user: newUser,
      loginType: nextLoginType,
      isKcbAuthed: kcbAuthed,
    };
    authCookie.value = JSON.stringify(payload);
    writeLocal(AUTH_KEY, payload);
  };

  const setSnsAuth = (snsToken, { name, id }) => {
    const payload = {
      accessToken: snsToken,
      user: {
        name,
        id,
      },
    };
    snsCookie.value = JSON.stringify(payload);
    writeLocal(SNS_KEY, payload);
  };

  const rememberUserIdx = (nextUserIdx) => {
    const value = String(nextUserIdx ?? "").trim();
    if (!value || value === "null" || !authData.value?.token) return;
    const payload = {
      ...authData.value,
      userIdx: value,
    };
    authCookie.value = JSON.stringify(payload);
    writeLocal(AUTH_KEY, payload);
  };

  const setKcbAuth = (status = true) => {
    const payload = {
      ...(authData.value || {}),
      isKcbAuthed: status,
    };
    authCookie.value = JSON.stringify(payload);
    writeLocal(AUTH_KEY, payload);
  };

  const clearAuth = () => {
    authCookie.value = null;
    snsCookie.value = null;
    writeLocal(AUTH_KEY, null);
    writeLocal(SNS_KEY, null);
  };

  if (import.meta.client) {
    syncStore(authCookie, AUTH_KEY, (value) => !!value?.token);
    syncStore(snsCookie, SNS_KEY, (value) => !!value?.accessToken);
  }

  return {
    token,
    user,
    loginType,
    isAuthenticated,
    snsAccessToken,
    snsUser,
    snsType,
    isKcbAuthed,
    userIdx,
    setAuth,
    rememberUserIdx,
    setSnsAuth,
    setKcbAuth,
    clearAuth,
  };
};
